













































































































































































































import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import {
  DocumentActionsModel,
  DocumentViewerScaleType,
  DocumentVersionType,
  KeyboardKeyCode,
  DocumentViewerToolbarOptionsModel,
  NotificationConstants,
  EditReferencesButtonTooltipModel,
  DocumentModel
} from '@/types';
import { DeviceHelper } from '@/mixins/deviceHelper';
import { LongPressEventType } from '@/types/enums/LongPressEventType';
import { FeatureToggleService } from '@/services/FeatureToggleService';
import { DocumentsToStore, EditReferencesRouteTypes, Workflow } from '@/types/constants';
import DocumentEmailModal from '@/components/shared/DocumentEmailModal.vue';
import DocumentEmailToMeModal from './DocumentEmailToMeModal.vue';
import MStorePrintDocument from './MStorePrintDocument.vue';
import { RouterUtils } from '@/utils';

export default Vue.extend({
  name: 'DocumentViewerToolbar',
  mixins: [DeviceHelper],
  props: {
    totalPages: {
      type: Number,
      required: true
    },
    imageProperties: {
      type: Object,
      required: true
    },
    imageLoaded: {
      type: String,
      required: true
    },
    documentActions: {
      type: Object as () => DocumentActionsModel,
      required: true
    },
    exportPending: {
      type: Boolean,
      required: true
    },
    deletePending: {
      type: Boolean,
      required: false
    },
    pageDeletePending: {
      type: Boolean,
      default: false
    },
    toolOptions: {
      type: Object as () => DocumentViewerToolbarOptionsModel,
      required: true
    },
    editReferencesButtonTooltip: {
      type: Object as () => EditReferencesButtonTooltipModel,
      required: false
    },
    isWorkflowJobLocked: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      scales: [0.12, 0.25, 0.37, 0.5, 0.62, 0.75, 0.87, 1, 1.25, 1.5, 1.75, 2, 2.5, 3, 3.5, 4, 4.5, 5],
      scale: 0,
      activeScaleOption: DocumentViewerScaleType.None as DocumentViewerScaleType,
      gotoPageNumber: 1,
      skipToPage: null,
      skipPagesIntervalId: null,
      skippingNextPages: false,
      skippingPreviousPages: false,
      skipPagesDelay: 170,
      showEditReferences: false,
      emailDocumentModalId: 'email-document-modal-id'
    };
  },
  methods: {
    ...mapActions('documentViewStore', ['loadPage']),
    async initialise(this: any) {
      const featureToggle = await FeatureToggleService.getFeatureToggle('EditReferences');
      if (featureToggle.toString().toLowerCase() === 'true' && this.toolOptions.showEditReferences) {
        this.showEditReferences = true;
      }
    },
    previousPage(this: any) {
      clearInterval(this.skipPagesIntervalId);
      this.skippingPreviousPages = false;
      if (this.currentPage === 1) {
        return;
      }
      if (this.skipToPage) {
        this.getPage(this.skipToPage);
        this.skipToPage = null;
        return;
      }
      this.getPage(this.currentPage - 1);
    },
    nextPage(this: any) {
      clearInterval(this.skipPagesIntervalId);
      this.skippingNextPages = false;
      if (this.currentPage === this.totalPages) {
        return;
      }
      if (this.skipToPage) {
        this.getPage(this.skipToPage);
        this.skipToPage = null;
        return;
      }
      this.getPage(this.currentPage + 1);
    },
    firstPage(this: any) {
      this.getPage(1);
    },
    lastPage(this: any) {
      this.getPage(this.totalPages);
    },
    navigateToPage(this: any) {
      this.gotoPageNumber = this.validateGotoPageNumber(this.gotoPageNumber);
    },
    validateGotoPageNumber(this: any, gotoPage: any, event: any) {
      if (isNaN(gotoPage)) {
        return this.currentPage;
      }
      const gotoPageNumber = Math.floor(Number(gotoPage));
      if (gotoPageNumber < 1 || gotoPageNumber > this.totalPages || this.currentPage === gotoPageNumber) {
        return this.currentPage;
      }
      this.getPage(gotoPageNumber);

      return gotoPageNumber;
    },
    getPage(this: any, pageNumber: number) {
      this.loadPage(pageNumber);
      this.$emit('setPageRotation', pageNumber);
    },
    zoomIn() {
      if (this.isMaxScale) {
        return;
      }
      this.setActiveScaleOption(DocumentViewerScaleType.ZoomIn);
      this.scale = this.getValidScale(this.nextScaleUp);
      this.setimageProperties();
    },
    zoomOut() {
      if (this.isMinScale) {
        return;
      }
      this.setActiveScaleOption(DocumentViewerScaleType.ZoomOut);
      this.scale = this.getValidScale(this.nextScaleDown);
      this.setimageProperties();
    },
    fitWidth() {
      this.setActiveScaleOption(DocumentViewerScaleType.FitWidth);
      this.scale = this.widthScale;
      this.setimageProperties();
    },
    fitHeight() {
      this.setActiveScaleOption(DocumentViewerScaleType.FitHeight);
      this.scale = this.heightScale;
      this.setimageProperties();
    },
    fitWithin() {
      this.setActiveScaleOption(DocumentViewerScaleType.FitWithin);
      if (this.widthScale > this.heightScale) {
        this.scale = this.heightScale;
      } else {
        this.scale = this.widthScale;
      }
      this.setimageProperties();
    },
    maximiseWidthWhilstStillSeeingTwoThirdsOfDocument() {
      this.setActiveScaleOption(DocumentViewerScaleType.None);

      const originalImageWidthToHeightRatio = this.imageProperties.originalWidth / this.imageProperties.originalHeight;

      const imageHeightIfFitWidth = this.imageProperties.containerWidth / originalImageWidthToHeightRatio;

      const twoThirdsOriginalImageHeight = (this.imageProperties.originalHeight * 2) / 3;

      if ((imageHeightIfFitWidth * 2) / 3 < this.imageProperties.containerHeight) {
        this.fitWidth();
        this.callDocumentViewerScaleMethod();
      } else {
        this.scale = this.getValidScale(this.imageProperties.containerHeight / twoThirdsOriginalImageHeight);
        this.setimageProperties();
      }
    },
    maximiseHeightWhilstStillSeeingTwoThirdsOfDocument() {
      this.setActiveScaleOption(DocumentViewerScaleType.None);

      const originalImageHeightToWidthRatio = this.imageProperties.originalHeight / this.imageProperties.originalWidth;

      const imageWidthIfFitHeight = this.imageProperties.containerHeight / originalImageHeightToWidthRatio;

      const twoThirdsOriginalImageWidth = (this.imageProperties.originalWidth * 2) / 3;

      if ((imageWidthIfFitHeight * 2) / 3 < this.imageProperties.containerWidth) {
        this.fitHeight();
      } else {
        this.scale = this.getValidScale(this.imageProperties.containerWidth / twoThirdsOriginalImageWidth);
        this.setimageProperties();
      }
    },
    rotateClockwise() {
      const degrees = (this.imageProperties.rotationDegrees + 90) % 360;
      this.imageProperties.rotationDegrees = degrees;
      this.callDocumentViewerScaleMethod();
      this.$emit('rotationChanged', degrees);
    },
    rotateAntiClockwise() {
      const degrees = (this.imageProperties.rotationDegrees + 270) % 360;
      this.imageProperties.rotationDegrees = degrees;
      this.callDocumentViewerScaleMethod();
      this.$emit('rotationChanged', degrees);
    },
    setActiveScaleOption(scaleOption: DocumentViewerScaleType) {
      this.activeScaleOption = scaleOption;
    },
    getValidScale(scale: number): number {
      if (scale < this.minScale) {
        return this.minScale;
      } else if (scale > this.maxScale) {
        return this.maxScale;
      } else {
        return scale;
      }
    },
    setimageProperties() {
      this.imageProperties.width = this.imageProperties.originalWidth * this.scale;
      this.imageProperties.height = this.imageProperties.originalHeight * this.scale;
      this.imageProperties.marginTop = this.marginTop;
      this.imageProperties.marginBottom = this.marginBottom;
      this.imageProperties.marginLeft = this.marginLeft;
      this.imageProperties.marginRight = this.marginRight;
      this.$emit('update:imageProperties', this.imageProperties);
    },
    callDocumentViewerScaleMethod() {
      switch (this.activeScaleOption) {
        case DocumentViewerScaleType.FitWithin:
          this.fitWithin();
          break;
        case DocumentViewerScaleType.FitWidth:
          this.fitWidth();
          break;
        case DocumentViewerScaleType.FitHeight:
          this.fitHeight();
          break;
        case DocumentViewerScaleType.ZoomIn:
        case DocumentViewerScaleType.ZoomOut:
          this.setimageProperties();
          break;
        case DocumentViewerScaleType.None:
        default:
          this.setInitialScale();
          break;
      }
    },
    onExportDocumentClicked() {
      this.$emit('exportDocument');
    },
    onDeleteDocumentPageClicked() {
      this.$emit('deleteDocumentPage');
    },
    onDeleteDocumentClicked() {
      this.$emit('deleteDocument');
    },
    onEditReferencesClicked() {
      sessionStorage.removeItem(EditReferencesRouteTypes.ReturnUrl);
      const routeWithPlaceholders = RouterUtils.replaceRouteParameterValuesWithPlaceholders(
        this.$router.currentRoute.path,
        this.$router.currentRoute.params,
        this.$router.currentRoute.matched && this.$router.currentRoute.matched[0] ? this.$router.currentRoute.matched[0].path : this.$router.currentRoute.path,
        [':cabinetId']
      );
      sessionStorage.setItem(EditReferencesRouteTypes.ReturnRoute, routeWithPlaceholders);
      this.$emit('editReferences');
    },
    validatePageNumber(this: any, event: any) {
      if (KeyboardKeyCode[event.keyCode]) {
        return;
      }

      const isTextSelected = window.getSelection();

      const newPageNumber = Number(isTextSelected?.type === 'Range' ? event.key : this.gotoPageNumber + event.key);
      if (Number.isNaN(newPageNumber) || newPageNumber > this.totalPages || newPageNumber < 1 || newPageNumber?.toString()?.length > this.totalPages?.toString()?.length) {
        event.preventDefault();
      }
    },
    skipNextPages(this: any, eventType: LongPressEventType) {
      if (eventType === LongPressEventType.Click || eventType === LongPressEventType.Release) {
        this.nextPage();
        return;
      }
      this.skippingNextPages = true;
      this.skipToPage = this.currentPage;
      const skipPages = (self: any) => {
        if (self.skipToPage < self.totalPages) {
          self.skipToPage = self.skipToPage + 1;
          self.gotoPageNumber = self.skipToPage;
        }
      };
      this.skipPagesIntervalId = setInterval(() => skipPages(this), this.skipPagesDelay);
    },
    skipPreviousPages(this: any, eventType: LongPressEventType) {
      if (eventType === LongPressEventType.Click || eventType === LongPressEventType.Release) {
        this.previousPage();
        return;
      }
      this.skippingPreviousPages = true;
      this.skipToPage = this.currentPage;
      const skipPages = (self: any) => {
        if (self.skipToPage > 1) {
          self.skipToPage = self.skipToPage - 1;
          self.gotoPageNumber = self.skipToPage;
        }
      };
      this.skipPagesIntervalId = setInterval(() => skipPages(this), this.skipPagesDelay);
    },
    setInitialScale() {
      if (this.imageProperties.originalHeight > this.imageProperties.originalWidth) {
        if (this.imageProperties.originalHeight < this.imageProperties.containerHeight) {
          this.fitHeight();
        } else {
          this.maximiseWidthWhilstStillSeeingTwoThirdsOfDocument();
        }
      } else {
        if (this.imageProperties.originalWidth < this.imageProperties.containerWidth && this.imageProperties.containerHeight > this.imageProperties.containerWidth) {
          this.fitWidth();
        } else {
          this.maximiseHeightWhilstStillSeeingTwoThirdsOfDocument();
        }
      }
    },
    openEmailDocumentModal() {
      this.$bvModal.show(this.emailDocumentModalId);
    },
    async showAsPdf(this: any) {
      this.$emit('showAsPdf');
    }
  },
  computed: {
    ...mapGetters('documentViewStore', ['document', 'currentPage', 'isPageLoading', 'versionBeingViewed', 'relatedDocumentBeingViewed']),
    totalPagesAppendText(): string {
      return `of ${this.totalPages > 0 ? this.totalPages : '?'}`;
    },
    canExport(this: any): boolean {
      return this.documentActions.export;
    },
    scalePercentage(this: any): number {
      return Math.floor(this.scale * 100);
    },
    scalePercentageLabel(): string {
      return this.scalePercentage === 0 ? '' : `${this.scalePercentage}%`;
    },
    widthScale(): number {
      const value = this.imageProperties.rotationDegrees % 180 === 0 ? this.imageProperties.originalWidth : this.imageProperties.originalHeight;
      return this.getValidScale(this.imageProperties.containerWidth / value);
    },
    heightScale(): number {
      const value = this.imageProperties.rotationDegrees % 180 === 0 ? this.imageProperties.originalHeight : this.imageProperties.originalWidth;
      return this.getValidScale(this.imageProperties.containerHeight / value);
    },
    nextScaleUp(): number {
      for (let i = 0; i < this.scales.length - 1; i++) {
        if (this.scale >= this.scales[i] && this.scale < this.scales[i + 1]) {
          return this.scales[i + 1];
        }
      }
      return this.maxScale;
    },
    nextScaleDown(): number {
      for (let i = 0; i < this.scales.length - 1; i++) {
        if (this.scale <= this.scales[i + 1] && this.scale > this.scales[i]) {
          return this.scales[i];
        }
      }
      return this.minScale;
    },
    maxScale(): number {
      return this.scales[this.scales.length - 1];
    },
    minScale(): number {
      return this.scales[0];
    },
    isMaxScale(): boolean {
      if (this.scale >= this.maxScale) {
        return true;
      }
      return false;
    },
    isMinScale(): boolean {
      if (this.scale <= this.minScale) {
        return true;
      }
      return false;
    },
    marginTop(): number {
      return -(this.imageProperties.rotationDegrees % 180 === 0 ? 0 : (this.imageProperties.height - this.imageProperties.width) / 2);
    },
    marginBottom(): number {
      return -(this.imageProperties.rotationDegrees % 180 === 0 ? 0 : (this.imageProperties.height - this.imageProperties.width) / 2);
    },
    marginLeft(): number {
      return -(this.imageProperties.rotationDegrees % 180 === 0 ? 0 : (this.imageProperties.width - this.imageProperties.height) / 2);
    },
    marginRight(): number {
      return -(this.imageProperties.rotationDegrees % 180 === 0 ? 0 : (this.imageProperties.width - this.imageProperties.height) / 2);
    },
    showVersionNumber(this: any): boolean {
      return this.versionBeingViewed.number && this.versionBeingViewed.type !== DocumentVersionType.Current;
    },
    showRelatedDocumentInfo(this: any): boolean {
      return this.relatedDocumentBeingViewed.documentSearchModel;
    },
    deletePageDisabled(this: any) {
      return this.deleteDisabled || !this.toolOptions.allowPageDelete || this.olderVersionBeingViewed || this.document.totalPages <= 1 || !this.document.isTif;
    },
    deleteDocumentDisabled(this: any) {
      return this.deleteDisabled || !this.toolOptions.allowDocumentDelete || this.document.inWorkflow;
    },
    deleteDisabled(this: any) {
      return this.isPageLoading || !this.documentActions.delete || this.deletePending || !this.document;
    },
    isPageLoadingOrDeleting(this: any): boolean {
      return this.isPageLoading || this.deletePending;
    },
    editReferencesDisabled(this: any) {
      return !this.document.canEditReferences || this.editReferencesButtonTooltip?.disabledTooltipMessage?.trim().length !== 0 || this.deletePending;
    },
    emailDisabled(this: any) {
      return this.isPageLoading || !this.documentActions.email;
    },
    isPrintDisabled(this: any) {
      return this.isPageLoading || !this.documentActions.print || !this.document.isImageOrPdfType;
    },
    printTooltip(this: any) {
      if (!this.documentActions.print) {
        return 'You do not have print permissions on this document type';
      }

      if (!this.document.isImageOrPdfType) {
        return 'This document cannot be converted to PDF, please export and print manually';
      }

      return 'Print Document';
    },
    getExportDocumentTooltip(this: any) {
      return this.documentActions.export ? 'Export Document' : NotificationConstants.NoExportPermissionForDocument;
    },
    viewPDFTooltip(this: any) {
      return this.canExport ? 'View PDF Document' : NotificationConstants.NoExportPermissionForDocument;
    },
    getDeletePageTooltip(this: any) {
      if (this.toolOptions && !this.toolOptions.allowPageDelete && this.toolOptions.pageDeleteNotAllowedTooltip) {
        return this.toolOptions.pageDeleteNotAllowedTooltip;
      }

      if (!this.document?.isTif || this.document?.totalPages <= 1) {
        return NotificationConstants.OnlyMultiPageTifsSupportPageDeletion;
      }

      if (this.olderVersionBeingViewed) {
        return NotificationConstants.CannotDeleteDocumentPagesFromOlderVersions;
      }

      return this.documentActions?.delete ? 'Delete Page' : NotificationConstants.NoDeletePermissionForDocument;
    },
    deleteDocumentTooltip(this: any): string {
      if (this.toolOptions && !this.toolOptions.allowDocumentDelete && this.toolOptions.documentDeleteNotAllowedTooltip) {
        return this.toolOptions.documentDeleteNotAllowedTooltip;
      }

      return !this.documentActions?.delete
        ? NotificationConstants.NoDeletePermissionForDocument
        : this.document?.inWorkflow
        ? NotificationConstants.CannotDeleteDocumentLinkedToWorkflow
        : 'Delete Document';
    },
    editReferencesTooltip(this: any): string {
      return this.document?.canEditReferences && !this.editReferencesButtonTooltip?.disabledTooltipMessage
        ? 'Edit References'
        : !this.document?.canEditReferences && this.editReferencesButtonTooltip?.disabledTooltipMessage
        ? this.editReferencesButtonTooltip?.important
          ? this.editReferencesButtonTooltip.disabledTooltipMessage
          : this.document.editReferencesButtonTooltip
        : !this.document?.canEditReferences
        ? this.document.editReferencesButtonTooltip
        : this.editReferencesButtonTooltip?.disabledTooltipMessage;
    },
    emailDocumentTooltip(this: any): string {
      return this.documentActions?.email ? 'Email Document' : NotificationConstants.NoEmailPermissionForDocument;
    },
    olderVersionBeingViewed(this: any): boolean {
      return this.versionBeingViewed?.type && this.versionBeingViewed.type !== DocumentVersionType.Current;
    },
    documentIsPdfFileType(this: any) {
      return (this.document as DocumentModel).fileExtension?.toUpperCase() === 'PDF';
    },
    canShowAsPdf(this: any) {
      return this.documentIsPdfFileType && this.routesAllowingShowAsPdf.includes(this.$route.name);
    }
  },
  watch: {
    imageProperties: {
      deep: true,
      handler() {
        this.callDocumentViewerScaleMethod();
      }
    },
    imageLoaded() {
      this.setInitialScale();
      this.gotoPageNumber = this.currentPage;
    },
    currentPage(this: any) {
      this.gotoPageNumber = this.currentPage;
    }
  },
  destroyed(this: any) {
    if (this.skipPagesIntervalId) {
      clearInterval(this.skipPagesIntervalId);
    }
  },
  mounted() {
    this.initialise();
  },
  created(this: any) {
    this.showPdfDocumentTooltip = 'Show as PDF';
    this.routesAllowingShowAsPdf = [DocumentsToStore.Base.name, Workflow.Job.name, Workflow.JobTab.name];
  },
  components: {
    DocumentEmailModal,
    DocumentEmailToMeModal,
    MStorePrintDocument
  }
});
